<script setup lang="ts">
/**
 * One numbered step of the checkout.
 *
 * Every section used to hand-roll its own header with a circle/check icon, so
 * the page read as eight unrelated cards rather than one ordered flow. Here the
 * step number is the state: pending, done, or blocked — and it is the same
 * shape everywhere.
 */
const props = defineProps<{
  step: number
  title: string
  subtitle?: string
  /** Requirements met — the number becomes a check. */
  done?: boolean
  /** Something in this step needs fixing; outranks `done` visually. */
  attention?: boolean
  /** Skippable step, flagged so nobody hunts for a missing requirement. */
  optional?: boolean
  /** Drops the body padding for content that draws its own rows (the plan table). */
  flushBody?: boolean
}>()

const cardUi = computed(() => ({
  root: 'overflow-hidden shadow-sm',
  header: 'bg-elevated/30 p-4 sm:px-5',
  body: props.flushBody ? 'p-0 sm:p-0' : 'p-4 sm:p-5',
}))
</script>

<template>
  <UCard :ui="cardUi">
    <template #header>
      <div class="flex items-start gap-3.5">
        <span
          :class="[
            'flex size-8 shrink-0 items-center justify-center rounded-full text-xs font-bold transition-colors',
            attention
              ? 'bg-warning text-inverted'
              : done
                ? 'bg-success text-inverted'
                : 'bg-elevated text-muted ring-1 ring-inset ring-default',
          ]"
        >
          <UIcon v-if="attention" name="i-lucide-alert-triangle" class="size-4" />
          <UIcon v-else-if="done" name="i-lucide-check" class="size-4" />
          <template v-else>{{ step }}</template>
        </span>

        <div class="min-w-0 flex-1">
          <div class="flex items-center gap-2 flex-wrap">
            <h2 class="text-base font-semibold leading-tight">{{ title }}</h2>
            <UBadge v-if="optional" size="sm" color="neutral" variant="soft">
              {{ $t('billing.page.checkout.steps.optional') }}
            </UBadge>
            <slot name="badge" />
          </div>
          <p
            v-if="subtitle || $slots.subtitle"
            class="mt-1 text-sm leading-relaxed text-toned"
          >
            <slot name="subtitle">{{ subtitle }}</slot>
          </p>
        </div>

        <div v-if="$slots.actions" class="shrink-0">
          <slot name="actions" />
        </div>
      </div>
    </template>

    <slot />
  </UCard>
</template>
